import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { applyOnecSyncDiff, getOnecSyncDiff } from '@/lib/api/client/onec-sync'
import type { OnecPieceDiff, OnecPieceDiffFilter, OnecPieceDiffLine } from '@/lib/api/schemas/onec-sync.schema'
import { unavailableReasonText } from '@/lib/warehouse/labels'

export const DIFF_PAGE_SIZE = 100

const CLASS_LABEL: Record<OnecPieceDiffLine['class'], string> = {
  create: 'Новое изделие',
  update: 'Изменение',
  unchanged: 'Без изменений',
  unmatched: 'Требует проверки',
}

const FILTERS: { value: OnecPieceDiffFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'sold', label: 'В продано' },
  { value: 'in_stock', label: 'В наличие' },
  { value: 'price', label: 'Цена' },
  { value: 'review', label: 'На проверку' },
]

const FIELD_LABEL: Record<string, string> = {
  article: 'Артикул',
  size: 'Размер',
  weightGrams: 'Вес, г',
  title: 'Название',
  fineness: 'Проба',
  material: 'Металл',
  onecItemType: 'Тип изделия',
  assay: 'Проба 1С',
  metalColour: 'Цвет металла',
  producer: 'Производитель',
  supplier: 'Поставщик',
  country: 'Страна',
  retailPrice: 'Цена в магазине',
  metalWeightGrams: 'Вес металла, г',
  metalWeightGramsReason: 'Вес металла',
  insertWeightGrams: 'Вес вставок, г',
  insertWeightGramsReason: 'Вес вставок',
  insertWeightCarats: 'Вес вставок, кт',
  insertWeightCaratsReason: 'Вес вставок в каратах',
  insertStones: 'Вставки',
  insertStonesReason: 'Вставки',
}

/** A `…Reason` change carries reason codes; the operator reads their sentences. */
function changeValue(field: string, value: string | null): string {
  if (value === null) return '—'
  return field.endsWith('Reason') ? unavailableReasonText(value) : value
}

const STATE_LABEL: Record<string, string> = {
  in_stock: 'в наличии',
  in_transit: 'в пути',
  sold: 'продано',
}

const REASON_LABEL: Record<string, string> = {
  onec_count_present: 'найдено при пересчёте в 1С',
  onec_count_stale: 'более новый пересчёт 1С прошёл по группе и изделие не нашёл',
  onec_receipt: 'поступление в 1С',
  onec_return: 'возврат в 1С',
  onec_sale: 'продажа в магазине по 1С',
  onec_issue: 'реализация или списание в 1С',
  onec_write_off: 'проведённое списание в 1С',
  storefront_return_received: 'возврат получен',
  kind_out_of_scope: 'в 1С это не ювелирное изделие',
  piece_merged: 'карточка объединена с другой',
  onec_count_absent: 'пересчёт 1С не нашёл изделие, списание не проведено',
  onec_marketplace_reservation: 'в 1С открыт резерв маркетплейса',
  onec_equal_timestamp_conflict: 'в 1С два противоречащих факта в одну секунду',
  onec_present_fact_without_balance: '1С видит изделие, но его нет на остатке',
  presence_without_proof: 'наличие ничем не подтверждено',
  silver_state_frozen: 'серебро: 1С меняет статус, но серебро не трогаем до решения по серебру',
  identity_incomplete: 'в 1С не хватает данных изделия',
  onec_new_piece_not_present: 'нового изделия нет в наличии по 1С',
  onec_piece_not_reported: 'в наличии в Forge, но 1С о нём ничего не сообщает',
  onec_balance_only: 'в 1С есть только остаток',
}

function reason(code: string): string {
  const [head] = code.split(':', 1)
  if (code.startsWith('storefront_') && head !== 'storefront_return_received') return 'заказ на витрине'
  return REASON_LABEL[head] ?? 'требует проверки'
}

function when(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Riga' }).format(new Date(value))
}

function count(diff: OnecPieceDiff, key: OnecPieceDiffLine['class']): number {
  return diff.counts[key] ?? 0
}

function actionable(line: OnecPieceDiffLine): boolean {
  return (line.class === 'create' || line.class === 'update') && !line.applied
}

function message(reason: unknown, fallback: string): string {
  return reason instanceof Error && reason.message.length > 0 ? reason.message : fallback
}

function LineView({ line, selected, onToggle }: { line: OnecPieceDiffLine; selected: boolean; onToggle: () => void }) {
  const changes = Object.entries(line.changes)
  return (
    <li className="flex gap-2 rounded-sm border border-rule bg-bone/30 px-3 py-2 text-[12px]" data-testid="onec-diff-line">
      {actionable(line)
        ? <input type="checkbox" className="mt-0.5 size-4 shrink-0" checked={selected} onChange={onToggle} aria-label={`Выбрать УИН ${line.uin}`} />
        : <span className="size-4 shrink-0" aria-hidden="true" />}
      <div className="grid min-w-0 flex-1 gap-0.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-semibold text-ink">УИН {line.uin}</span>
          <span className={line.class === 'unmatched' ? 'text-amber-700' : 'text-ink-2'}>
            {line.applied ? 'Применено' : CLASS_LABEL[line.class]}
          </span>
        </div>
        {line.stateTo !== null && (
          <div>
            Статус: {line.stateFrom !== null ? `${STATE_LABEL[line.stateFrom] ?? line.stateFrom} → ` : ''}
            {STATE_LABEL[line.stateTo]}{line.stateReason !== null && ` (${reason(line.stateReason)})`}
          </div>
        )}
        {changes.map(([field, change]) => (
          <div key={field} className="break-words">{FIELD_LABEL[field] ?? field}: {changeValue(field, change.from)} → {changeValue(field, change.to)}</div>
        ))}
        {line.notes.map((note) => <div key={note} className="text-ink-muted">{reason(note)}</div>)}
      </div>
    </li>
  )
}

export function OnecSyncDiff({ diff: first, onApplied }: { diff: OnecPieceDiff | null; onApplied: () => Promise<void> }) {
  const [diff, setDiff] = useState<OnecPieceDiff | null>(first)
  const [filter, setFilter] = useState<OnecPieceDiffFilter>('all')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setDiff(first)
    setFilter('all')
    setSelected(new Set())
  }, [first])

  const show = async (nextFilter: OnecPieceDiffFilter, nextOffset: number) => {
    setError(null)
    try {
      setDiff(await getOnecSyncDiff({ filter: nextFilter, offset: nextOffset, limit: DIFF_PAGE_SIZE }))
      setFilter(nextFilter)
    } catch (reason: unknown) {
      setError(message(reason, 'Не удалось загрузить строки сверки.'))
    }
  }

  if (diff === null) {
    return (
      <div>
        <h3 className="text-[13px] font-semibold text-ink">Сверка с 1С</h3>
        <p className="mt-1 text-[12px] text-ink-muted">Сверки изделий и остатков ещё не было.</p>
      </div>
    )
  }

  const unapplied = count(diff, 'create') + count(diff, 'update') - diff.appliedCount
  const pageActionable = diff.lines.filter(actionable).map((line) => line.uin)
  const toggle = (uin: string) => {
    const next = new Set(selected)
    if (next.has(uin)) next.delete(uin)
    else next.add(uin)
    setSelected(next)
  }
  const selectPage = () => setSelected(new Set([...selected, ...pageActionable]))

  const apply = async (uins?: string[]) => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const queued = await applyOnecSyncDiff(diff.operationId, uins)
      setNotice(`Применение поставлено в очередь: ${queued.title}.`)
      setSelected(new Set())
      await onApplied()
    } catch (reason: unknown) {
      setError(message(reason, 'Не удалось применить сверку.'))
    } finally {
      setBusy(false)
    }
  }

  const lastLine = Math.min(diff.offset + diff.lines.length, diff.linesTotal)
  return (
    <div className="grid min-w-0 gap-1 border-t border-rule pt-3">
      <div>
        <h3 className="text-[13px] font-semibold text-ink">Сверка с 1С</h3>
        <p className="mt-0.5 text-[12px] text-ink-muted" data-testid="onec-diff-summary">
          Новых {count(diff, 'create')},
          изменений {count(diff, 'update')}, без изменений {count(diff, 'unchanged')}, требуют проверки {count(diff, 'unmatched')}
          {diff.appliedCount > 0 && ` · применено ${diff.appliedCount}`}
        </p>
      </div>
      <details data-testid="onec-diff-details">
        <summary className="w-fit cursor-pointer py-2 text-[12px] text-ink-muted">Строки и применение</summary>
        <div className="grid min-w-0 gap-2 pt-1">
          <p className="text-[12px] text-ink-muted">
            {when(diff.createdAt)} · {diff.mode === 'full' ? 'полная' : 'изменения за окно'}
          </p>
          <p className="max-w-prose text-[12px] text-ink-muted">
            Применяются только новые изделия и изменения, которые подтверждает 1С. Ничего не удаляется; строки «Требует проверки» остаются как есть.
          </p>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Фильтр строк сверки">
            {FILTERS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                className={`min-h-9 rounded-full border px-3 text-[12px] ${filter === value ? 'border-gold bg-gold/10 text-ink' : 'border-rule text-ink-2'}`}
                onClick={() => { void show(value, 0) }}
              >
                {label} · {diff.filterCounts[value] ?? 0}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="btn min-h-10" onClick={selectPage} disabled={busy || pageActionable.length === 0}>
              Выбрать на странице
            </button>
            {(unapplied > 0 || selected.size > 0) && (
              <button type="button" className={`btn min-h-10 ${selected.size > 0 ? 'btn-primary' : 'text-ink-muted'}`} onClick={() => { void apply([...selected]) }} disabled={busy || selected.size === 0}>
                {busy ? 'Применяем…' : `Применить выбранные (${selected.size})`}
              </button>
            )}
            {unapplied > 0 && (
              <button type="button" className="btn min-h-10" onClick={() => { void apply() }} disabled={busy || unapplied <= 0}>
                Применить все ({Math.max(unapplied, 0)})
              </button>
            )}
            {diff.applied !== null && (
              <Link className="text-[12px] text-gold underline" to="/operations">Применение от {when(diff.applied.createdAt)} в «Операциях»</Link>
            )}
          </div>
          {error !== null && <p role="alert" className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</p>}
          {notice !== null && <p role="status" className="text-[12px] text-st-delivered">{notice}</p>}
          {diff.lines.length > 0
            ? (
              <ul className="grid gap-1.5" aria-label="Строки сверки с 1С">
                {diff.lines.map((line) => (
                  <LineView key={line.uin} line={line} selected={selected.has(line.uin)} onToggle={() => toggle(line.uin)} />
                ))}
              </ul>
            )
            : <p className="text-[12px] text-ink-muted">Строк с таким фильтром нет.</p>}
          {diff.linesTotal > 0 && (
            <nav className="flex flex-wrap items-center justify-between gap-2 text-[12px]" aria-label="Страницы сверки">
              <span data-testid="onec-diff-range">Строки {diff.offset + 1}–{lastLine} из {diff.linesTotal}</span>
              <span className="flex gap-2">
                <button type="button" className="btn min-h-9" disabled={diff.offset === 0} onClick={() => { void show(filter, Math.max(diff.offset - DIFF_PAGE_SIZE, 0)) }}>
                  Назад
                </button>
                <button type="button" className="btn min-h-9" disabled={lastLine >= diff.linesTotal} onClick={() => { void show(filter, diff.offset + DIFF_PAGE_SIZE) }}>
                  Дальше
                </button>
              </span>
            </nav>
          )}
        </div>
      </details>
    </div>
  )
}
